import { env } from '@/env';

export const BASE_API_URL = env.NEXT_PUBLIC_API_BASE_URL;

export const ACCESS_TOKEN_COOKIE_NAME = 'accessToken' as const;
export const REFRESH_TOKEN_COOKIE_NAME = 'refreshToken' as const;

export const apiRoutes = {
  auth: {
    me: 'auth/me',
    login: 'auth/login',
    signup: 'auth/register',
    logout: 'auth/logout',
    googleAuth: 'auth/google',
    linkedinAuth: 'auth/linkedin',
    ssoCallback: 'auth/sso/callback',
    resetPassword: 'auth/reset-password',
    sendRecoveryOtp: 'auth/send-recovery-otp',
    validateRecoveryOtp: 'auth/validate-recovery-otp',
    verifyEmailOtp: 'otp/verify',
    sendEmailOtp: 'otp/send',
    refreshToken: 'auth/refresh',
    changePassword: 'auth/change-password',
  },
  user: {
    getById: (userId: string) => `user/${userId}`,
    checkUsername: (username: string) =>
      `user/check-username?username=${username}`,
    searchMentions: (query: string, limit: number = 8) =>
      `user/mention-search?query=${encodeURIComponent(query)}&limit=${limit}`,
    deleteAccount: 'user/delete',
    deleteAccountGoogle: 'user/delete/google',
    requestSaveRecoveryEmail: 'user/request-save-recovery-email',
    saveRecoveryEmail: 'user/save-recovery-email',
    requestMigratePrimaryEmail: 'user/request-migrate-primary-email',
    migratePrimaryEmail: 'user/migrate-primary-email',
  },
  devices: {
    getAll: 'devices',
    revoke: (deviceFingerprint: string) => `devices/${deviceFingerprint}`,
    signOutAll: 'devices/sign-out-all',
    getSecurityAnalysis: 'devices/security-analysis',
  },
  post: {
    getPosts: (params?: string) => `post${params ? `?${params}` : ''}`,
    getById: (postId: string) => `post/${postId}`,
    getLocations: (query: string) => `post/locations?query=${query}`,
    search: (params: string) => `post/search?${params}`,
    create: 'post',
    delete: (postId: string) => `post/${postId}`,
    update: (postId: string) => `post/${postId}`,
    updateStatus: (postId: string) => `post/${postId}/status`,
  },
  profile: {
    create: 'profile',
    update: 'profile/edit',
    getByUsername: (username: string) => `profile/${username}`,
    getCertifications: (userId: string) => `profile/certifications/${userId}`,
    getPortfolio: (userId: string) => `profile/portfolio/${userId}`,
    getReviews: (userId: string, params: string) =>
      `profile/reviews/${userId}?${params}`,
    uploadCertification: 'profile/certifications',
    deleteCertification: (certificationId: string) =>
      `profile/certifications/${certificationId}`,
    addPortfolio: 'profile/portfolio',
    deletePortfolio: (portfolioId: string) =>
      `profile/portfolio/${portfolioId}`,
    getPosts: (userId: string, params?: string) =>
      `profile/posts/${userId}${params ? `?${params}` : ''}`,
  },
  category: {
    get: 'categories',
  },
  file: {
    generateUploadUrl: 'file/generate-upload-url',
  },
  message: {
    getChats: 'message/chats',
    getChatById: (chatId: string, params?: string) =>
      `message/chat/${chatId}${params ? `?${params}` : ''}`,
    getChatIdByPartnerId: (partnerId: string) => `message/chat-id/${partnerId}`,
    createChat: 'message/create-chat',
    sendMessage: 'message',
    generateUploadUrl: 'file/generate-upload-url',
    createServiceRequest: 'message/service-request',
    getServiceRequest: (serviceRequestId: string) =>
      `message/service-request/${serviceRequestId}`,
    respondToService: 'message/service-response',
    startServiceWork: 'message/start-service',
    markServiceCompleted: 'message/complete-service',
    confirmServiceCompletion: 'message/confirm-complete-service',
    leaveReview: 'message/leave-review',
    dismissReview: (serviceRequestId: string) =>
      `message/dismiss-review/${serviceRequestId}`,
    createDispute: 'message/dispute-service',
  },
  ably: {
    getToken: 'ably/token',
  },
  notification: {
    getAll: (params?: string) => `notifications${params ? `?${params}` : ''}`,
    getUnreadCount: 'notifications/unread-count',
    markAsRead: (id: string) => `notifications/${id}/read`,
    markAllAsRead: 'notifications/read-all',
    delete: (id: string) => `notifications/${id}`,
  },
  notificationSettings: {
    get: 'notification-settings',
    update: 'notification-settings',
    getCategories: 'notification-settings/categories',
  },
  privacySettings: {
    get: 'privacy-settings',
    update: 'privacy-settings',
  },
  wallet: {
    get: (params?: string) => `wallet${params ? `?${params}` : ''}`,
  },
  rewards: {
    social: {
      follow: 'rewards/social/follow',
      share: 'rewards/social/share',
      status: 'rewards/social/status',
    },
  },
  transactions: {
    getAll: 'transactions',
  },
  contact: {
    submit: 'contact-form',
  },
  feedback: {
    submit: 'feedback',
  },
  blockedUsers: {
    get: 'blocked-users',
    unblock: 'blocked-users/unblock',
  },
  tickets: {
    getAll: (params?: string) => `tickets${params ? `?${params}` : ''}`,
    getAllAssignedTickets: (params?: string) =>
      `tickets/my-tickets${params ? `?${params}` : ''}`,
    getById: (id: string) => `tickets/${id}`,
    update: (id: string) => `tickets/${id}`,
    delete: (id: string) => `tickets/${id}`,
  },
  admin: {
    getAll: 'admin/admin-user-list',
    getUserList: (params?: string) =>
      `admin/user-list${params ? `?${params}` : ''}`,
    changeUserRole: (userId: string) => `admin/change-user-role/${userId}`,
    getRecentActivity: 'admin/dashboard/recent-admin-activity',
    getAllActivity: (params?: string) =>
      `admin/activity${params ? `?${params}` : ''}`,
    getNorthStarChartData: 'admin/dashboard/graphs',
    statSummary: 'admin/dashboard/summary',
    getUserDetails: (id: string) => `admin/user/${id}`,
    addNote: (id: string) => `admin/add-note/${id}`,
    performAction: (id: string) => `admin/admin-action-on-user/${id}`,
    disputeTransferCoins: 'admin/dispute-transfer-coins',
    transferCoins: 'admin/transfer-coins',
    getCoinTransferList: (params?: string) =>
      `admin/coin-transfer-list${params ? `?${params}` : ''}`,
    getCoinTransferDetails: (id: string) => `admin/coin-transfer/${id}`,
    processCoinTransfer: 'admin/process-coin-transfer',
  },
  moderation: {
    createReport: 'post-reports',
    getBannedWords: (params?: string) =>
      `moderation/banned-words${params ? `?${params}` : ''}`,
    createBannedWord: 'moderation/banned-words',
    deleteBannedWord: (id: string) => `moderation/banned-words/${id}`,
    // Old flagged posts endpoints (Flag collection-based)
    getFlaggedPosts: (params?: string) =>
      `moderation/flagged-posts${params ? `?${params}` : ''}`,
    getFlaggedPostById: (id: string) => `moderation/flagged-posts/${id}`,
    clearFlag: (id: string) => `moderation/flag/${id}`,
    deletePost: (id: string) => `moderation/delete-post/${id}`,
    // New flagged posts endpoints (Post.isFlagged counter-cache)
    getNewFlaggedPosts: (params?: string) =>
      `moderation/posts/flagged${params ? `?${params}` : ''}`,
    unflagPost: (postId: string) => `moderation/posts/${postId}/unflag`,
    softDeletePost: (postId: string) =>
      `moderation/posts/${postId}/soft-delete`,
    addModerationNote: (postId: string) => `moderation/posts/${postId}/note`,
    getAppeals: (params?: string) =>
      `moderation/appeals${params ? `?${params}` : ''}`,
    getAppealById: (id: string) => `moderation/appeals/${id}`,
    approveAppeal: (id: string) => `moderation/appeals/${id}/approve`,
    rejectAppeal: (id: string) => `moderation/appeals/${id}/reject`,
    // Old reports endpoints (individual PostReport docs)
    getReports: (params?: string) =>
      `moderation/reports${params ? `?${params}` : ''}`,
    getReportById: (id: string) => `moderation/reports/${id}`,
    // New reported posts endpoints (counter-cache grouped by post)
    getReportedPosts: (params?: string) =>
      `post-reports${params ? `?${params}` : ''}`,
    getReportedPostDetails: (postId: string) => `post-reports/${postId}`,
  },
};

export const getApiUrl = (path: string) => {
  return `${BASE_API_URL}/api/${path}`;
};
